import { Phone, Mail, Globe, MapPin, Clock, Zap, Send } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOUNDING_BADGES = {
  business: { label: "Founding Business Member", bg: "bg-[#14B8A6]/10", text: "text-[#14B8A6]", border: "border-[#14B8A6]/30" },
  corporate: { label: "Founding Corporate Partner", bg: "bg-[#6366F1]/10", text: "text-[#6366F1]", border: "border-[#6366F1]/30" },
  industrial: { label: "Founding Industrial Partner", bg: "bg-[#DC2626]/10", text: "text-[#DC2626]", border: "border-[#DC2626]/30" },
};

const FoundingBadge = ({ business, size = "sm" }) => {
  const category = business.founding_category || (business.membership_tier === "founding" ? "business" : null);
  if (!business.is_founding && business.membership_tier !== "founding") return null;
  const badge = FOUNDING_BADGES[category] || FOUNDING_BADGES.business;
  const cls = size === "sm"
    ? `inline-flex items-center gap-1 ${badge.bg} ${badge.text} border ${badge.border} text-[10px] px-2 py-0.5 rounded-full font-semibold`
    : `inline-flex items-center gap-1.5 ${badge.bg} ${badge.text} border ${badge.border} text-xs px-3 py-1 rounded-full font-semibold`;
  return <span className={cls} data-testid="founding-badge"><Zap className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} />{badge.label}</span>;
};

export const BusinessCard = ({ business, onClick }) => {
  return (
    <div 
      className="business-card bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 card-shadow cursor-pointer"
      onClick={onClick}
      data-testid={`business-card-${business.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-heading text-xl font-semibold text-[#1A1915]">
            {business.name}
          </h3>
          <p className="text-[#5C5A52] text-sm flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3" />
            {business.town}
          </p>
        </div>
        <span className="bg-[#2C4A3B] text-white text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">
          {business.category}
        </span>
      </div>

      {/* Description */}
      <p className="text-[#5C5A52] text-sm mb-4 line-clamp-2">
        {business.description}
      </p>

      {/* Contact Info */}
      <div className="flex flex-wrap gap-3 text-xs text-[#5C5A52]">
        {business.phone && (
          <a href={`tel:${business.phone}`} className="flex items-center gap-1 hover:text-[#2C4A3B]" onClick={(e) => e.stopPropagation()}>
            <Phone className="w-3 h-3" /><span>{business.phone}</span>
          </a>
        )}
        {business.email && (
          <a href={`mailto:${business.email}`} className="flex items-center gap-1 hover:text-[#2C4A3B]" onClick={(e) => e.stopPropagation()}>
            <Mail className="w-3 h-3" /><span>Email</span>
          </a>
        )}
        {business.website && (
          <a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#2C4A3B]" onClick={(e) => e.stopPropagation()}>
            <Globe className="w-3 h-3" /><span>Website</span>
          </a>
        )}
      </div>

      {/* Founding Badge */}
      {(business.is_founding || business.membership_tier === "founding") && (
        <div className="mt-4 pt-3 border-t border-[#D1CFC5]">
          <FoundingBadge business={business} />
        </div>
      )}
    </div>
  );
};

export const BusinessCardFull = ({ business, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="business-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#1A1915]">
              {business.name}
            </h2>
            {business.owner_name && (
              <p className="text-[#5C5A52] text-sm mt-1">
                Owned by {business.owner_name}
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className="text-[#5C5A52] hover:text-[#1A1915] text-2xl leading-none"
            data-testid="close-business-modal"
          >
            ×
          </button>
        </div>

        {/* Category & Town */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="bg-[#2C4A3B] text-white text-xs px-3 py-1 rounded-full">
            {business.category}
          </span>
          <span className="bg-[#E5E2D9] text-[#1A1915] text-xs px-3 py-1 rounded-full flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {business.town}
          </span>
          <FoundingBadge business={business} size="md" />
        </div>

        {/* Description */}
        <p className="text-[#5C5A52] mb-6">
          {business.description}
        </p>

        {/* Contact Details */}
        <div className="space-y-3 border-t border-[#D1CFC5] pt-4">
          {business.phone && (
            <a 
              href={`tel:${business.phone}`}
              className="flex items-center gap-3 text-[#1A1915] hover:text-[#2C4A3B]"
            >
              <Phone className="w-4 h-4 text-[#2C4A3B]" />
              <span>{business.phone}</span>
            </a>
          )}
          {business.email && (
            <a 
              href={`mailto:${business.email}`}
              className="flex items-center gap-3 text-[#1A1915] hover:text-[#2C4A3B]"
            >
              <Mail className="w-4 h-4 text-[#2C4A3B]" />
              <span>{business.email}</span>
            </a>
          )}
          {business.website && (
            <a 
              href={business.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#1A1915] hover:text-[#2C4A3B]"
            >
              <Globe className="w-4 h-4 text-[#2C4A3B]" />
              <span>{business.website}</span>
            </a>
          )}
          {business.address && (
            <div className="flex items-center gap-3 text-[#5C5A52]">
              <MapPin className="w-4 h-4 text-[#2C4A3B]" />
              <span>{business.address}</span>
            </div>
          )}
          {business.hours && (
            <div className="flex items-center gap-3 text-[#5C5A52]">
              <Clock className="w-4 h-4 text-[#2C4A3B]" />
              <span>{business.hours}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-6 flex flex-col gap-3">
          {business.phone && (
            <a
              href={`tel:${business.phone}`}
              className="w-full bg-[#2C4A3B] text-white font-semibold py-3 px-6 rounded-lg border-2 border-[#1A1915] btn-shadow flex items-center justify-center gap-2"
              data-testid="contact-business-btn"
            >
              <Phone className="w-4 h-4" />
              Contact Business
            </a>
          )}
          <RequestQuoteButton business={business} onClose={onClose} />
        </div>
      </div>
    </div>
  );
};

// ==================== REQUEST A QUOTE ====================
const RequestQuoteButton = ({ business }) => {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "services", description: "", budget_range: "" });

  const serviceTypes = [
    { value: "contractors", label: "Contractor Work" },
    { value: "tourism", label: "Tourism / Activity" },
    { value: "services", label: "Service" },
    { value: "products", label: "Product Purchase" },
    { value: "equipment_rental", label: "Equipment Rental" },
    { value: "other", label: "Other" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) { toast.error("Please fill in name, email, and description"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/commerce/requests`, {
        business_id: business.id,
        requester_name: form.name,
        requester_email: form.email,
        requester_phone: form.phone || null,
        service_type: form.service_type,
        description: form.description,
        budget_range: form.budget_range || null,
        town: business.town,
      });
      toast.success("Quote request sent! The business will respond shortly.");
      setShowForm(false);
      setForm({ name: "", email: "", phone: "", service_type: "services", description: "", budget_range: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
    setSubmitting(false);
  };

  if (!showForm) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setShowForm(true); }}
        className="w-full bg-[#D4A017] hover:bg-[#E5B945] text-[#1A1915] font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
        data-testid="request-quote-btn"
      >
        <Send className="w-4 h-4" />
        Request a Quote
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} className="border-2 border-[#D4A017]/30 rounded-xl p-4 bg-[#D4A017]/5 space-y-3" data-testid="request-quote-form">
      <p className="font-semibold text-[#1A1915] text-sm">Request a Quote from {business.name}</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name *" className="border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" required data-testid="quote-name" />
        <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" placeholder="Email *" className="border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" required data-testid="quote-email" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone (optional)" className="border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" data-testid="quote-phone" />
        <select value={form.service_type} onChange={e => setForm({...form, service_type: e.target.value})} className="border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" data-testid="quote-type">
          {serviceTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe what you need *" rows={3} className="w-full border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" required data-testid="quote-description" />
      <input value={form.budget_range} onChange={e => setForm({...form, budget_range: e.target.value})} placeholder="Budget range (e.g. $500-$1000)" className="w-full border border-[#D1CFC5] rounded-lg px-3 py-2 text-sm bg-white" data-testid="quote-budget" />
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="flex-1 bg-[#D4A017] hover:bg-[#E5B945] text-[#1A1915] font-semibold py-2 rounded-lg text-sm transition-colors" data-testid="quote-submit">
          {submitting ? "Sending..." : "Send Request"}
        </button>
        <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#D1CFC5] rounded-lg text-sm text-[#5C5A52] hover:bg-[#F7F6F2]">Cancel</button>
      </div>
    </form>
  );
};

export default BusinessCard;
